import { AuthContext } from '@/shared/context/auth-context/AuthContext';
import AdminPanelPage from '@/views/layouts/admin-panel/AdminPanelPage';
import { Box, Paper, Stack } from '@mui/material';
import React, { useContext, useState } from 'react';
import { Button as DxButton } from 'devextreme-react/button';
import { Popup } from 'devextreme-react/popup';
import ResetPasswordForm from './components/reset-password-form.component';
import { useChangePassword } from '@/queries/auth';
import { ChangePasswordRequest } from '@/api/auth/types';
import useToastMessage from '@/shared/context/toast-message-context';

export const AccountPageComponent: React.FC = () => {
	const { user } = useContext(AuthContext);

	const [isPopupVisible, setPopupVisibility] = useState(false);
	const [isLoading, setLoading] = useState(false);

	const togglePopup = () => {
		setPopupVisibility(!isPopupVisible);
	};

	const [changePasswordData, setChangePasswordData] = useState({
		currentPassword: '',
		newPassword: '',
	});

	const { mutate: changePassword } = useChangePassword(changePasswordData);
	const { dispatch } = useToastMessage();

	const onFormSubmit = async (data: ChangePasswordRequest) => {
		setLoading(true);
		setChangePasswordData(data);

		await new Promise<void>((resolve, reject) => {
			changePassword(undefined, {
				onSuccess: () => {
					dispatch({ type: 'success', content: 'Password changed successfully.' });
					resolve();
				},
				onError: (err) => {
					dispatch({
						type: 'error',
						content: `${err.response?.data?.message || 'Failed to change password.'}`,
					});
					reject(err);
				},
				onSettled: () => {
					setPopupVisibility(false);
					setLoading(false);
				},
			});
		});
	};

	const popupContent = () =>
		isPopupVisible && (
			<ResetPasswordForm onSubmit={onFormSubmit} loading={isLoading}></ResetPasswordForm>
		);

	return (
		<AdminPanelPage title="Account Details">
			<Popup
				width={400}
				height="auto"
				showTitle={true}
				title="Change Password"
				visible={isPopupVisible}
				hideOnOutsideClick={true}
				onHiding={togglePopup}
				contentRender={popupContent}
				showCloseButton={true}
			></Popup>

			<Paper elevation={2} sx={{ p: 3, width: 'fit-content', alignSelf: 'flex-start' }}>
				{user ? (
					<>
						<Box display="flex" alignItems="center" gap={2}>
							<Box
								sx={{
									width: 56,
									height: 56,
									borderRadius: '50%',
									bgcolor: 'primary.main',
									color: 'primary.contrastText',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									fontWeight: 700,
									fontSize: 18,
								}}
							>
								{((user?.firstName?.[0] ?? '') + (user?.lastName?.[0] ?? '')).toUpperCase() || '?'}
							</Box>
							<Box>
								<Box component="div" sx={{ fontSize: 18, fontWeight: 600 }}>
									{`${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() || user?.email}
								</Box>
								<Box component="div" sx={{ color: 'text.secondary', fontSize: 14 }}>
									{user?.role}
								</Box>
							</Box>
						</Box>

						<Box sx={{ my: 2, height: 1, bgcolor: 'divider' }} />

						<Box
							display="grid"
							sx={{ gridTemplateColumns: '120px 1fr', rowGap: 1.25, columnGap: 2 }}
						>
							<Box component="div" sx={{ color: 'text.secondary' }}>
								Email
							</Box>
							<Box component="div">{user?.email}</Box>

							<Box component="div" sx={{ color: 'text.secondary' }}>
								Role
							</Box>
							<Box component="div" sx={{ textTransform: 'capitalize' }}>
								{user?.role}
							</Box>
						</Box>

						<Box sx={{ my: 2, height: 1, bgcolor: 'divider' }} />

						<Stack>
							<DxButton type="default" text="Change Password" onClick={togglePopup} />
						</Stack>
					</>
				) : (
					<Box sx={{ color: 'text.secondary' }}>No account information available.</Box>
				)}
			</Paper>
		</AdminPanelPage>
	);
};
